import { Card, Modal, Segmented, Skeleton } from "antd"
import React, { Suspense, lazy, useCallback, useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"

import { isDeletedAccountRow } from "../../../../constants/deleted-account-ui.ts"
import { ROLE_LABEL_STATUSES } from "../../../../constants/role-labels.ts"
import { nextPageUrl } from "../../../../redux/shared/paged-list.ts"
import type { RootState } from "../../../../store.ts"
import {
  selectCompanyIsMultiBranch,
  selectIsCompanyMode,
  selectIsOwner
} from "../../../Branches/redux/branches.slice.ts"
import { extractParamsFromNextUrl } from "../../../../utils/form/extract-params-from-next-url.ts"
import { clearUsersFilters } from "../../redux/team-members.slice"
import {
  fetchTeamMembers,
  performActionTeamMember
} from "../../redux/team-members.thunks"
import { type TeamMembersFilters } from "../../redux/team-members.types.ts"
import type {
  TeamMember,
  TeamMemberFormIntent,
  TeamMemberMutationData
} from "../../types/types.ts"
import { TeamMembersListFilters } from "./TeamMembersListFilters.tsx"
import { TeamOrganizationView } from "./TeamOrganizationView.tsx"

const TeamMembersListMobile = lazy(
  () => import("./TeamMembersMobile/TeamMembersListMobile")
)

const TeamMembersEditableTable = lazy(
  () => import("./TeamMembersEditableTable/TeamMembersEditableTable")
)

const MEMBER_REMOVAL_ADMIN_LABELS = new Set<string>([
  ROLE_LABEL_STATUSES.ADMIN,
  ROLE_LABEL_STATUSES.OWNER
])

// Section 6 — Organization (the HQ hierarchy) and All employees (the flat,
// editable table) are two views over the same filtered member set, so they
// share one filters bar and one `filters` value; only the view itself is
// local, unpersisted UI state.
type TeamMembersView = "all_employees" | "organization"

interface TeamMembersProps extends ConnectedProps<typeof connector> {
  filters: TeamMembersFilters
  ordering: string | null
  currentUserId: string
  onSelectUser: (user: TeamMember, intent?: TeamMemberFormIntent) => void
  onPageChange: (page: number) => void
  onOrderingChange: (ordering: string | null) => void
  onFilterChange: (filters: Partial<TeamMembersFilters>) => void
  /** Asks for the first page again, after it failed to load. */
  onRetry?: () => void
  onSaveUser: (data: TeamMemberMutationData) => Promise<void>
  onDeleteUser: (id: string) => Promise<void>
}

const TeamMembers = ({
  fetching,
  currentUserId,
  saveFetching,
  filterOptions,
  filters,
  ordering,
  result,
  getUsers,
  teamPermissions,
  currentUserLabelCode,
  isMultiBranch,
  isOwner,
  isCompanyMode,
  onSelectUser,
  isMobile,
  performActionUser,
  clearFilters,
  onPageChange,
  onOrderingChange,
  onFilterChange,
  onRetry,
  onSaveUser,
  onDeleteUser
}: TeamMembersProps) => {
  const { count, results } = result
  const [view, setView] = useState<TeamMembersView>("all_employees")

  // Organization shows the whole company's HQ hierarchy and every branch's
  // own roster — that's HQ-owner territory, not something a Branch Manager
  // (or the Owner themself, while looking at one specific branch) should
  // be offered. `isMultiBranch` alone used to gate this and let both
  // through.
  const canUseOrganizationView = isMultiBranch && isOwner && isCompanyMode

  useEffect(() => {
    if (!canUseOrganizationView && view === "organization") {
      setView("all_employees")
    }
  }, [canUseOrganizationView, view])

  const handleLoadMore = useCallback(() => {
    const url = nextPageUrl(result)
    if (!url) return
    // The server's own url carries the page and every filter it was built
    // with, so nothing here has to reconstruct them.
    getUsers({
      ...extractParamsFromNextUrl<TeamMembersFilters>(url),
      isMore: true
    })
  }, [result?.next, result?.retryNext, getUsers])

  const editAllowed = teamPermissions?.team?.team_edit
  const inviteAllowed = teamPermissions?.features?.team_invite_user
  const modifyPermissionsAllowed =
    teamPermissions?.features?.team_modify_permissions
  const deleteAllowed = Boolean(
    teamPermissions?.team?.team_delete &&
    currentUserLabelCode &&
    MEMBER_REMOVAL_ADMIN_LABELS.has(currentUserLabelCode)
  )

  const handleSelectRow = useCallback(
    (data: TeamMember, type: string) => {
      if (isDeletedAccountRow(data)) return

      switch (type) {
        case "edit":
          return onSelectUser(data)
        case "permissions":
          return onSelectUser(data, "permissions")
        case "block":
          return Modal.confirm({
            centered: true,
            title: "Confirm team member blocking",
            content: "Are you sure you want to block this team member?",
            cancelText: "Cancel",
            okText: "Confirm",
            okButtonProps: { type: "primary" },
            onOk() {
              performActionUser({ id: data.id, action: type })
            }
          })
        case "unblock":
          return Modal.confirm({
            centered: true,
            title: "Confirm team member activation",
            content: "Are you sure you want to activate this team member?",
            cancelText: "Cancel",
            okText: "Confirm",
            okButtonProps: { type: "primary" },
            onOk() {
              performActionUser({ id: data.id, action: type })
            }
          })
        case "remove":
          return Modal.confirm({
            centered: true,
            title: "Remove team member",
            content:
              "This revokes the member's access. Their history is preserved. If they still hold active leads, projects or tasks, you'll be prompted to reassign those first.",
            cancelText: "Cancel",
            okText: "Remove",
            okButtonProps: { danger: true },
            onOk() {
              void onDeleteUser(data.id)
            }
          })
        default:
          performActionUser({ id: data.id, action: type })
      }
    },
    [onSelectUser, performActionUser, onDeleteUser, teamPermissions]
  )

  const handleChangeSearch = useCallback(
    (value: string | null) => {
      onFilterChange({ q: value })
    },
    [onFilterChange]
  )

  return (
    <Suspense fallback={<Skeleton.Node active className={"size-full"} />}>
      {isMobile ? (
        <TeamMembersListMobile
          currentUserId={currentUserId}
          users={results}
          count={count}
          page={filters?.page}
          loading={fetching || saveFetching}
          filterOptions={filterOptions}
          clearFilters={clearFilters}
          filters={filters}
          pageSize={filters?.page_size}
          //
          editAllowed={editAllowed}
          inviteAllowed={inviteAllowed}
          deleteAllowed={deleteAllowed}
          modifyPermissionsAllowed={modifyPermissionsAllowed}
          //
          onPageChange={onPageChange}
          onSearchChange={handleChangeSearch}
          onFiltersChange={onFilterChange}
          onSelectRow={handleSelectRow}
        />
      ) : (
        <>
          <Card size="small">
            <TeamMembersListFilters
              filters={filters}
              filterOptions={filterOptions}
              onSearchChange={handleChangeSearch}
              onFilterChange={onFilterChange}
            />
          </Card>

          {/* A single-operation company has nothing to organize by branch —
              the whole Organization view is branch terminology, so it's not
              offered at all, and the view silently stays "all_employees".
              Multi-branch alone isn't enough either: Organization is HQ-
              owner territory, hidden for a Branch Manager and for the
              Owner's own view of one specific branch. */}
          {canUseOrganizationView && (
            <Segmented
              className="self-start"
              value={view}
              onChange={(value) => setView(value as TeamMembersView)}
              options={[
                { label: "All employees", value: "all_employees" },
                { label: "Organization", value: "organization" }
              ]}
            />
          )}

          {canUseOrganizationView && view === "organization" ? (
            <TeamOrganizationView
              filters={filters}
              onSelectUser={(user) => handleSelectRow(user, "edit")}
            />
          ) : (
            <TeamMembersEditableTable
              currentUserId={currentUserId}
              members={results}
              next={result?.next}
              retryNext={result?.retryNext}
              loadFailed={result?.loadFailed}
              onRetry={onRetry}
              onClearFilters={() =>
                onFilterChange({ labels: null, role: null, status: null })
              }
              onLoadMore={handleLoadMore}
              count={count}
              page={filters?.page}
              pageSize={filters?.page_size}
              filters={filters}
              ordering={ordering}
              loading={fetching || saveFetching}
              filterOptions={filterOptions}
              isMultiBranch={isMultiBranch}
              //
              editAllowed={editAllowed}
              inviteAllowed={inviteAllowed}
              deleteAllowed={deleteAllowed}
              modifyPermissionsAllowed={modifyPermissionsAllowed}
              //
              onSelectRow={handleSelectRow}
              onEditRow={onSaveUser}
              onOrderingChange={onOrderingChange}
            />
          )}
        </>
      )}
    </Suspense>
  )
}

const mapStateToProps = (state: RootState) => ({
  fetching: state.users.fetching,
  result: state.users.result,
  isMobile: state.nav.isMobile,

  filterOptions: state.users.filterOptions,
  saveFetching: state.users.saveFetching,
  currentUserLabelCode: state.auth.userInfo?.role?.label_code,
  teamPermissions: state?.auth?.permissions?.codes?.team_management,
  // The real backend company-mode flag, not the global workspace
  // selector's current position — single-operation companies must never
  // see branch terminology, regardless of which branch is selected.
  isMultiBranch: selectCompanyIsMultiBranch(state),
  isOwner: selectIsOwner(state),
  isCompanyMode: selectIsCompanyMode(state)
})

const mapDispatchToProps = {
  performActionUser: performActionTeamMember,
  clearFilters: clearUsersFilters,
  getUsers: fetchTeamMembers
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(TeamMembers)
